import { Check, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Popover } from './Popover';
import { t } from '@lumen/platform';

export interface MenuItem {
  type?: 'item';
  id: string;
  label: string;
  icon?: ReactNode;
  description?: string;
  hint?: string;
  checked?: boolean;
  danger?: boolean;
  disabled?: boolean;
  keywords?: string[];
  right?: ReactNode;
  onSelect?: () => void;
  /** Nested menu shown on hover / ArrowRight. */
  submenu?: () => MenuEntry[];
  testId?: string;
}

export type MenuEntry = MenuItem | { type: 'group'; label: string; id?: string } | { type: 'divider'; id?: string } | { type: 'custom'; id: string; render: () => ReactNode };

export function isItem(e: MenuEntry): e is MenuItem {
  return e.type === undefined || e.type === 'item';
}

export function filterEntries(entries: MenuEntry[], query: string): MenuEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return entries;
  const out: MenuEntry[] = [];
  let pendingGroup: MenuEntry | null = null;
  for (const e of entries) {
    if (e.type === 'group') {
      pendingGroup = e;
      continue;
    }
    if (!isItem(e)) continue;
    const hay = [e.label, ...(e.keywords ?? [])].map((s) => s.toLowerCase());
    if (hay.some((h) => h.includes(q) || h.replace(/\s+/g, '').includes(q.replace(/\s+/g, '')))) {
      if (pendingGroup) {
        out.push(pendingGroup);
        pendingGroup = null;
      }
      out.push(e);
    }
  }
  return out;
}

export interface MenuProps {
  entries: MenuEntry[];
  onClose: () => void;
  /** Shows a search field at the top that filters items. */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Controlled query (e.g. typed in the editor after "/"). Disables the internal search field. */
  query?: string;
  /** Controlled active index (keyboard handled by the owner). */
  activeIndex?: number;
  onActiveIndexChange?: (i: number) => void;
  emptyText?: string;
  footer?: ReactNode;
  header?: ReactNode;
  testId?: string;
  autoFocus?: boolean;
}

/** Keyboard-navigable menu list (Notion-style). */
export function Menu({
  entries,
  onClose,
  searchable,
  searchPlaceholder = 'Search…',
  query: controlledQuery,
  activeIndex: controlledActive,
  onActiveIndexChange,
  emptyText = t('No results'),
  footer,
  header,
  testId,
  autoFocus = true,
}: MenuProps) {
  const [internalQuery, setInternalQuery] = useState('');
  const query = controlledQuery ?? internalQuery;
  const visible = useMemo(() => filterEntries(entries, query), [entries, query]);
  const items = useMemo(() => visible.filter(isItem).filter((i) => !i.disabled), [visible]);
  const [internalActive, setInternalActive] = useState(0);
  const active = controlledActive ?? internalActive;
  const setActive = (i: number) => {
    if (onActiveIndexChange) onActiveIndexChange(i);
    else setInternalActive(i);
  };
  const [openSub, setOpenSub] = useState<string | null>(null);
  const itemRefs = useRef(new Map<string, HTMLDivElement>());
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (controlledActive === undefined) setInternalActive(0);
  }, [query, controlledActive]);

  useEffect(() => {
    if (!autoFocus) return;
    if (searchable && controlledQuery === undefined) inputRef.current?.focus();
    else if (controlledQuery === undefined) rootRef.current?.focus();
  }, [autoFocus, searchable, controlledQuery]);

  useEffect(() => {
    const item = items[active];
    if (item) itemRefs.current.get(item.id)?.scrollIntoView({ block: 'nearest' });
  }, [active, items]);

  const select = (item: MenuItem) => {
    if (item.disabled) return;
    if (item.submenu) {
      setOpenSub(item.id);
      return;
    }
    item.onSelect?.();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (openSub) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(items.length ? (active + 1) % items.length : 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(items.length ? (active - 1 + items.length) % items.length : 0);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[active];
      if (item) select(item);
    } else if (e.key === 'ArrowRight') {
      const item = items[active];
      if (item?.submenu) {
        e.preventDefault();
        setOpenSub(item.id);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  return (
    <div className="lm-menu" ref={rootRef} tabIndex={-1} onKeyDown={onKeyDown} data-testid={testId} role="menu" style={{ outline: 'none' }}>
      {header}
      {searchable && controlledQuery === undefined && (
        <div className="lm-menu-search">
          <input
            ref={inputRef}
            className="lm-input"
            placeholder={searchPlaceholder}
            value={internalQuery}
            onChange={(e) => setInternalQuery(e.target.value)}
            data-testid={testId ? `${testId}-search` : undefined}
          />
        </div>
      )}
      <div className="lm-menu-scroll">
        {items.length === 0 && <div className="lm-menu-empty">{emptyText}</div>}
        {visible.map((e, idx) => {
          if (e.type === 'group') return <div key={`g-${e.label}-${idx}`} className="lm-menu-group-label">{e.label}</div>;
          if (e.type === 'divider') return <div key={`d-${idx}`} className="lm-menu-divider" />;
          if (e.type === 'custom') return <div key={e.id}>{e.render()}</div>;
          const item = e;
          const i = items.indexOf(item);
          return (
            <div
              key={item.id}
              ref={(el) => {
                if (el) itemRefs.current.set(item.id, el);
                else itemRefs.current.delete(item.id);
              }}
              className="lm-menu-item"
              role="menuitem"
              data-active={i === active && i !== -1}
              data-danger={item.danger || undefined}
              aria-disabled={item.disabled || undefined}
              data-testid={item.testId ?? `menu-item-${item.id}`}
              onMouseMove={() => {
                if (i !== -1 && i !== active) setActive(i);
                if (item.submenu) setOpenSub(item.id);
                else if (openSub) setOpenSub(null);
              }}
              onMouseDown={(ev) => ev.preventDefault()}
              onClick={() => select(item)}
            >
              {item.icon !== undefined && <span className="lm-menu-item-icon">{item.icon}</span>}
              <span className="lm-menu-item-label">
                {item.label}
                {item.description && <span className="lm-menu-item-desc">{item.description}</span>}
              </span>
              {item.right}
              {item.hint && <span className="lm-menu-item-hint">{item.hint}</span>}
              {item.checked && <Check size={14} className="lm-menu-item-check" />}
              {item.submenu && <ChevronRight size={14} className="lm-menu-item-hint" />}
              {item.submenu && openSub === item.id && (
                <Popover open anchor={itemRefs.current.get(item.id) ?? null} placement="right-start" offset={6} onClose={() => setOpenSub(null)} width={220}>
                  <Menu entries={item.submenu()} onClose={() => { setOpenSub(null); rootRef.current?.focus(); }} />
                </Popover>
              )}
            </div>
          );
        })}
      </div>
      {footer && <div className="lm-menu-footer">{footer}</div>}
    </div>
  );
}

/** Convenience: a popover containing a menu. */
export function PopoverMenu(props: MenuProps & { open: boolean; anchor: import('./Popover').Anchor; width?: number; placement?: import('@floating-ui/react').Placement }) {
  const { open, anchor, width = 265, placement, ...menu } = props;
  return (
    <Popover open={open} anchor={anchor} onClose={menu.onClose} width={width} {...(placement ? { placement } : {})}>
      <Menu {...menu} />
    </Popover>
  );
}
